<div class="imp-header">
  <h2 mat-dialog-title>Import meter data</h2>
  <button mat-icon-button mat-dialog-close aria-label="Close"><mat-icon>close</mat-icon></button>
</div>

<mat-dialog-content class="imp-content">
  <div *ngIf="error" class="imp-alert imp-bad imp-error" role="alert">{{ error }}</div>
  <mat-tab-group [selectedIndex]="tab" (selectedIndexChange)="onTabChange($event)">

    <!-- ============================== IMPORT ============================== -->
    <mat-tab label="Import">
      <div class="imp-pane">

        <div *ngIf="busy" class="imp-busy">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <span>{{ busy }}...</span>
        </div>

        <!-- pick files -->
        <section *ngIf="!preview && !result" class="imp-pick">
          <p class="imp-lead">Upload exports from FusionSolar or Enacto, or a standard reading CSV.</p>
          <p class="imp-muted">Add a whole store's files at once (the store total and each asset): they are checked and imported together.
            Nothing is written until you have checked where each meter goes and pressed Import.</p>
          <div class="imp-drop" [class.imp-drop-over]="dragging"
               (dragover)="onDragOver($event)" (dragleave)="dragging = false" (drop)="onDrop($event)"
               (click)="fileInput.click()" role="button" tabindex="0" (keydown.enter)="fileInput.click()">
            <mat-icon>upload_file</mat-icon>
            <span>Drop files here, or click to choose</span>
            <span class="imp-muted">.xlsx, .csv or a .zip of them</span>
            <input #fileInput type="file" accept=".xlsx,.csv,.zip" multiple hidden (change)="onFileSelected($event)">
          </div>
          <ul *ngIf="files.length" class="imp-picked">
            <li *ngFor="let f of files">
              <mat-icon>description</mat-icon>
              <span class="imp-picked-name">{{ f.name }}</span>
              <span class="imp-muted">{{ f.size / 1024 | number:'1.0-0' }} KB</span>
              <button mat-icon-button (click)="removeFile(f)" [disabled]="!!busy" aria-label="Remove" matTooltip="Remove"><mat-icon>close</mat-icon></button>
            </li>
          </ul>
          <div class="imp-pick-row">
            <button mat-flat-button color="primary" [disabled]="!files.length || !!busy" (click)="upload()">
              Upload and check{{ files.length > 1 ? ' ' + files.length + ' files' : '' }}
            </button>
            <button *ngIf="files.length" mat-button [disabled]="!!busy" (click)="files = []">Clear</button>
          </div>
        </section>

        <!-- review -->
        <ng-container *ngIf="preview && !result">

          <!-- the upload at a glance -->
          <section class="imp-card imp-file">
            <div class="imp-file-head">
              <mat-icon class="imp-file-icon">description</mat-icon>
              <div class="imp-file-name">
                <strong [matTooltip]="preview.files.length ? preview.fileName : ''">{{ preview.files.length ? preview.files.length + ' files' : preview.fileName }}</strong>
                <span class="imp-muted">
                  · {{ preview.series.length }} meter{{ preview.series.length === 1 ? '' : 's' }}
                  <ng-container *ngIf="preview.readingCount"> · {{ preview.readingCount | number }} readings
                    · {{ preview.firstInterval | date:'dd/MM/yyyy' }} to {{ preview.lastInterval | date:'dd/MM/yyyy' }}</ng-container>
                  <ng-container *ngIf="!preview.readingCount"> · no readings, a layout to set up</ng-container>
                </span>
              </div>
              <span class="imp-badge" [ngClass]="'imp-' + preview.validationStatus.toLowerCase()" [matTooltip]="checkHelp(preview.validationStatus)">{{ preview.validationStatus }}</span>
            </div>

            <div *ngIf="preview.validationStatus === 'FAIL'" class="imp-alert imp-bad">
              <mat-icon>block</mat-icon>
              <span>The checks found a problem that would make the figures wrong, so this can't be imported. It's listed below.</span>
            </div>

            <div *ngIf="preview.sameFileBefore" class="imp-alert imp-warn">
              <mat-icon>history</mat-icon>
              <span>Already imported as upload {{ preview.sameFileBefore.runId }}
                ({{ preview.sameFileBefore.status }}, {{ preview.sameFileBefore.createdUtc | date:'dd/MM/yyyy HH:mm' }}, {{ preview.sameFileBefore.createdBy }}).
                Importing again replaces the same readings; nothing is added twice.</span>
            </div>

            <!-- data checks: problems open, the rest one line each -->
            <div class="imp-checks">
              <p *ngIf="!checkGroups.length" class="imp-ok"><mat-icon>check_circle</mat-icon> Data checks found nothing to flag.</p>
              <details *ngFor="let g of checkGroups" class="imp-check" [ngClass]="'imp-check-' + g.level.toLowerCase()" [open]="g.level === 'ERROR'">
                <summary>
                  <mat-icon>{{ g.level === 'ERROR' ? 'error' : g.level === 'WARN' ? 'warning' : 'info' }}</mat-icon>
                  <span class="imp-check-title">{{ g.title }}</span>
                  <span class="imp-muted">{{ g.findings.length }} meter{{ g.findings.length === 1 ? '' : 's' }}</span>
                </summary>
                <ul class="imp-finds">
                  <li *ngFor="let f of g.findings">
                    <div><strong>{{ findingMeter(f) }}</strong>: {{ f.message }}</div>
                    <ul *ngIf="f.examples.length" class="imp-examples">
                      <li *ngFor="let e of f.examples">{{ e }}</li>
                    </ul>
                  </li>
                </ul>
              </details>
            </div>

            <details *ngIf="preview.files.length || preview.notes.length" class="imp-more">
              <summary>Files and notes</summary>
              <table *ngIf="preview.files.length" class="imp-table">
                <thead><tr><th>File</th><th>Meter</th><th>Readings</th></tr></thead>
                <tbody>
                  <tr *ngFor="let f of preview.files">
                    <td>{{ f.name }}<div class="imp-muted">{{ f.format }}</div></td>
                    <td><div *ngFor="let m of f.meters">{{ m }}</div></td>
                    <td>{{ f.readings | number }}</td>
                  </tr>
                </tbody>
              </table>
              <ul *ngIf="preview.notes.length" class="imp-notes">
                <li *ngFor="let n of preview.notes"><mat-icon>info</mat-icon><span>{{ n }}</span></li>
              </ul>
            </details>
          </section>

          <!-- each store: site, then its meters as a tree with the group and category each goes to -->
          <section *ngFor="let st of stores" class="imp-card imp-store">
            <div class="imp-store-head">
              <div class="imp-store-source">
                <span class="imp-label">From {{ preview.sourceSystem | titlecase }}</span>
                <strong>{{ cleanSourceSite(st.sourceSite) }}</strong>
              </div>
              <mat-icon class="imp-arrow">arrow_forward</mat-icon>
              <div class="imp-store-target">
                <span class="imp-label">Carbon site</span>
                <strong *ngIf="storeTarget(st); else noSite">{{ storeTarget(st) }}</strong>
                <ng-template #noSite><span class="imp-text-warn">Choose below</span></ng-template>
              </div>
            </div>

            <!-- the site, chosen once for the store -->
            <div *ngIf="st.lead as lead" class="imp-site-pick">
              <mat-button-toggle-group [value]="choice(lead).siteMode" (change)="setSiteMode(lead, $event.value)" class="imp-toggle">
                <mat-button-toggle value="existing">Existing site</mat-button-toggle>
                <mat-button-toggle value="new">New site</mat-button-toggle>
              </mat-button-toggle-group>

              <ng-container *ngIf="choice(lead).siteMode === 'existing'">
                <div *ngIf="lead.siteSuggestions.length && !choice(lead).siteId" class="imp-suggestions">
                  <span class="imp-muted">Suggested:</span>
                  <button *ngFor="let g of lead.siteSuggestions" mat-stroked-button [matTooltip]="g.why" (click)="pickSite(lead, g.siteId)">
                    {{ g.name }} ({{ g.siteId }})
                  </button>
                </div>
                <mat-form-field appearance="outline" class="imp-wide">
                  <mat-label>Carbon site</mat-label>
                  <input matInput [(ngModel)]="choice(lead).siteFilter" [matAutocomplete]="auto" placeholder="Type at least 2 letters of the name, or the site number"
                         [readonly]="!!choice(lead).siteId">
                  <button *ngIf="choice(lead).siteId" matSuffix mat-icon-button aria-label="Clear" (click)="clearSite(lead)"><mat-icon>close</mat-icon></button>
                  <mat-autocomplete #auto="matAutocomplete" (optionSelected)="pickSite(lead, $event.option.value)" [displayWith]="siteNameFn">
                    <mat-option *ngFor="let o of filteredSites(lead)" [value]="o.id">{{ o.name }} ({{ o.id }})</mat-option>
                  </mat-autocomplete>
                </mat-form-field>
              </ng-container>

              <div *ngIf="choice(lead).siteMode === 'new'" class="imp-two">
                <mat-form-field appearance="outline">
                  <mat-label>New site name</mat-label>
                  <input matInput maxlength="50" [(ngModel)]="choice(lead).newSiteName" (ngModelChange)="newSiteNameChanged(lead)">
                </mat-form-field>
                <div>
                  <mat-form-field *ngIf="choice(lead).portalMode === 'existing'" appearance="outline" class="imp-wide">
                    <mat-label>Portal</mat-label>
                    <mat-select [(ngModel)]="choice(lead).newSitePortalId" (selectionChange)="newSiteNameChanged(lead)">
                      <mat-option *ngFor="let p of lookups.portals" [value]="p.id">{{ p.name }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field *ngIf="choice(lead).portalMode === 'new'" appearance="outline" class="imp-wide">
                    <mat-label>New portal name</mat-label>
                    <input matInput maxlength="50" [(ngModel)]="choice(lead).newPortalName" (ngModelChange)="newSiteNameChanged(lead)">
                    <mat-hint>Name only. Add its users and logo in Carbon Admin.</mat-hint>
                  </mat-form-field>
                  <button mat-button color="primary" class="imp-link" (click)="setPortalMode(lead, choice(lead).portalMode === 'new' ? 'existing' : 'new')">
                    {{ choice(lead).portalMode === 'new' ? 'Use an existing portal' : 'Create a new portal instead' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- the meters -->
            <div class="imp-tree" role="list">
              <div class="imp-tree-head" aria-hidden="true">
                <span>Meter</span><span>Logical group</span><span>Portal category</span><span></span>
              </div>
              <ng-container *ngFor="let row of st.rows">
                <div class="imp-node" role="listitem" [ngClass]="'imp-state-' + row.s.state.toLowerCase()">
                  <div class="imp-node-meter" [style.padding-left.px]="row.depth * 20">
                    <span *ngIf="row.depth > 1" class="imp-elbow" aria-hidden="true">└</span>
                    <mat-checkbox [(ngModel)]="choice(row.s).include" (change)="changed()" [matTooltip]="meterLabel(row.s)">
                      <span [class.imp-strong]="row.depth <= 1">{{ row.name }}</span>
                    </mat-checkbox>
                    <span class="imp-muted imp-node-total" *ngIf="!row.s.layoutOnly">{{ row.s.total | number:'1.0-0' }} {{ row.s.unit }}</span>
                    <span class="imp-muted imp-node-total" *ngIf="row.s.layoutOnly" [class.imp-text-warn]="row.s.assetStatus !== 'Active'">no readings{{ row.s.assetStatus && row.s.assetStatus !== 'Active' ? ', ' + row.s.assetStatus : '' }}</span>
                  </div>
                  <div class="imp-node-group">
                    <ng-container *ngIf="choice(row.s).include">{{ groupLabel(row.s) }}</ng-container>
                    <span *ngIf="row.s.stack && choice(row.s).include" class="imp-chip" [ngClass]="'imp-chip-' + row.s.stack" [matTooltip]="stackHelp(row.s)">{{ stackLabel(row.s) }}</span>
                  </div>
                  <div class="imp-node-cat">
                    <ng-container *ngIf="choice(row.s).include">
                      <span *ngIf="row.s.match" class="imp-muted">as before</span>
                      <span *ngIf="!row.s.match">{{ categoryName(row.s) || '—' }}</span>
                    </ng-container>
                  </div>
                  <div class="imp-node-end">
                    <mat-icon *ngIf="warnings(row.s).length && choice(row.s).include" class="imp-text-warn imp-small-icon"
                              [matTooltip]="warningText(row.s)">warning</mat-icon>
                    <span class="imp-state" [ngClass]="'imp-state-text-' + row.s.state.toLowerCase()" [matTooltip]="row.s.reason || ''">{{ stateLabel(row.s) }}</span>
                    <button *ngIf="canEdit(row.s)" mat-icon-button (click)="toggleEdit(row.s)" [attr.aria-label]="'Edit ' + row.name" matTooltip="Change its group or category">
                      <mat-icon>{{ editing.has(row.s.seriesId) ? 'expand_less' : 'edit' }}</mat-icon>
                    </button>
                  </div>
                </div>

                <div *ngIf="row.s.state === 'Refused' && choice(row.s).include" class="imp-node-note imp-text-bad" [style.margin-left.px]="row.depth * 20 + 28">{{ row.s.reason }}</div>

                <!-- group and category for one meter, on request -->
                <div *ngIf="canEdit(row.s) && editing.has(row.s.seriesId)" class="imp-edit" [style.margin-left.px]="row.depth * 20 + 28">
                  <div class="imp-two">
                    <div>
                      <mat-radio-group [(ngModel)]="choice(row.s).groupMode" (change)="changed()" class="imp-radios">
                        <mat-radio-button value="new">New logical group</mat-radio-button>
                        <mat-radio-button value="existing" [disabled]="choice(row.s).siteMode === 'new' || !emptyGroups(choice(row.s)).length">
                          Existing empty group
                        </mat-radio-button>
                      </mat-radio-group>
                      <div *ngIf="groupHint(choice(row.s))" class="imp-muted imp-hint">{{ groupHint(choice(row.s)) }}</div>
                      <mat-form-field *ngIf="choice(row.s).groupMode === 'new'" appearance="outline" class="imp-wide">
                        <mat-label>Group name</mat-label>
                        <input matInput maxlength="100" [(ngModel)]="choice(row.s).newLogicalGroupName" (ngModelChange)="changed()">
                      </mat-form-field>
                      <mat-form-field *ngIf="choice(row.s).groupMode === 'existing'" appearance="outline" class="imp-wide">
                        <mat-label>Group (only ones with no meter yet)</mat-label>
                        <mat-select [(ngModel)]="choice(row.s).logicalGroupId" (selectionChange)="changed()">
                          <mat-option *ngFor="let g of emptyGroups(choice(row.s))" [value]="g.logicalGroupId">{{ g.name }} ({{ g.readingTypeName || 'no unit' }})</mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>
                    <mat-form-field appearance="outline" class="imp-cat-field">
                      <mat-label>Portal category</mat-label>
                      <mat-select [(ngModel)]="choice(row.s).categoryId" (selectionChange)="changed()">
                        <mat-option [value]="undefined">None</mat-option>
                        <mat-optgroup label="Suits imported meters (Consumption report)">
                          <mat-option *ngFor="let c of recommendedCategories" [value]="c.id">{{ categoryLabel(c, true) }}</mat-option>
                        </mat-optgroup>
                        <mat-optgroup label="Other categories">
                          <mat-option *ngFor="let c of otherCategories" [value]="c.id">{{ categoryLabel(c, true) }}</mat-option>
                        </mat-optgroup>
                      </mat-select>
                      <mat-hint>Customers see the category name only.</mat-hint>
                    </mat-form-field>
                  </div>
                </div>
              </ng-container>
            </div>

            <details *ngIf="!dirty" class="imp-more">
              <summary>Step by step, what importing will do</summary>
              <div *ngFor="let row of st.rows">
                <ng-container *ngIf="row.s.actions.length && choice(row.s).include">
                  <div class="imp-plan-meter">{{ row.s.sourceMeter === 'site total' ? 'Store total' : row.s.sourceMeter }}</div>
                  <ul class="imp-plan">
                    <li *ngFor="let a of row.s.actions">
                      <span class="imp-tag" [ngClass]="'imp-tag-' + a.kind">{{ actionLabel(a) }}</span>
                      <span>{{ a.text }}</span>
                    </li>
                  </ul>
                </ng-container>
              </div>
            </details>
          </section>

          <div *ngIf="newSiteNames.length" class="imp-alert imp-warn">
            <mat-checkbox [(ngModel)]="confirmNewSites" (change)="changed()">
              I've checked that <strong>{{ newSiteNames.join(', ') }}</strong> isn't already in Carbon under another name<ng-container *ngIf="newPortalNames.length">,
              and that the portal <strong>{{ newPortalNames.join(', ') }}</strong> doesn't exist either</ng-container>, and want the import to create it.
            </mat-checkbox>
          </div>

          <div *ngIf="dirty" class="imp-alert imp-warn"><mat-icon>refresh</mat-icon><span>You've changed something. Press <b>Check plan</b> before importing.</span></div>
          <p class="imp-muted imp-note"><mat-icon>info</mat-icon> Readings already stored for the same times are replaced, never added twice. Every import can be undone from History.</p>
        </ng-container>

        <!-- result -->
        <section *ngIf="result" class="imp-result">
          <div class="imp-summary">
            <div>
              <strong>Upload {{ result.runId }}</strong><span *ngIf="resultSites.length === 1"> to {{ resultSites[0] }}</span>
              <span class="imp-muted" *ngFor="let c of resultCounts"> · <span [class.imp-text-bad]="c.bad">{{ c.count }} {{ c.label }}</span></span>
            </div>
            <span class="imp-badge" [ngClass]="result.status === 'Applied' || result.status === 'Undone' ? 'imp-pass' : result.status === 'Failed' ? 'imp-fail' : 'imp-warn'" [matTooltip]="statusHelp(result.status)">{{ result.status }}</span>
          </div>

          <div class="imp-tiles">
            <div *ngIf="resultReadings.stored"><b>{{ resultReadings.stored | number }}</b><span>readings stored<ng-container *ngIf="resultReadings.replaced">, {{ resultReadings.replaced | number }} replacing earlier ones</ng-container></span></div>
            <div *ngIf="resultReadings.hours"><b>{{ resultReadings.hours | number }}</b><span>hourly figures built</span></div>
            <div *ngFor="let c of resultCreated" class="imp-tile-text"><mat-icon>add_circle</mat-icon><span>{{ c }}</span></div>
          </div>

          <div *ngIf="resultAttention.length" class="imp-alert imp-warn">
            <mat-icon>priority_high</mat-icon>
            <div>
              <b>Needs attention</b>
              <ul class="imp-attention"><li *ngFor="let a of resultAttention">{{ a }}</li></ul>
            </div>
          </div>
          <div *ngIf="preview && actionableChecks" class="imp-muted imp-note">
            <mat-icon>warning</mat-icon> The data checks flagged {{ actionableChecks }} thing{{ actionableChecks === 1 ? '' : 's' }} before import: {{ actionableTitles }}.
          </div>

          <table class="imp-table imp-result-table">
            <thead><tr><th>Meter</th><th>Logical group</th><th>Readings</th><th>Status</th></tr></thead>
            <tbody>
              <tr *ngFor="let r of result.series">
                <td>{{ resultMeter(r) }}</td>
                <td>{{ r.logicalGroupName }}<div *ngIf="r.siteName && resultSites.length > 1" class="imp-muted">{{ r.siteName }}</div></td>
                <td>{{ r.rawRowsInserted + r.rawRowsReplaced ? (r.rawRowsInserted + r.rawRowsReplaced | number) : '' }}</td>
                <td [ngClass]="r.status === 'Failed' || r.status === 'UndoFailed' ? 'imp-text-bad' : ''">{{ r.status }}</td>
              </tr>
            </tbody>
          </table>

          <details class="imp-more">
            <summary>Technical details</summary>
            <ul *ngIf="resultNotes.length" class="imp-notes">
              <li *ngFor="let m of resultNotes"><mat-icon>info</mat-icon><span>{{ m }}</span></li>
            </ul>
            <div *ngFor="let r of result.series" class="imp-tech">
              <strong>{{ resultMeter(r) }}</strong>
              <ul class="imp-actions"><li *ngFor="let st of r.steps">{{ st }}</li></ul>
              <div *ngIf="r.detail" class="imp-text-bad">{{ r.detail }}</div>
            </div>
          </details>
        </section>
      </div>
    </mat-tab>

    <!-- ============================== HISTORY ============================== -->
    <mat-tab label="History">
      <div class="imp-pane">
        <table class="imp-table">
          <thead><tr><th>#</th><th>File</th><th>Readings</th><th>Period</th><th>By</th><th>Status</th><th></th></tr></thead>
          <tbody>
          <tr *ngFor="let r of runs">
            <td>{{ r.runId }}</td>
            <td>{{ r.fileName }}<div class="imp-muted">{{ r.format }}</div></td>
            <td>{{ r.readingCount | number }}<div><span class="imp-badge imp-badge-sm" [ngClass]="'imp-' + r.validationStatus.toLowerCase()" [matTooltip]="checkHelp(r.validationStatus)">{{ r.validationStatus }}</span></div></td>
            <td>{{ r.firstInterval | date:'dd/MM/yyyy' }} to {{ r.lastInterval | date:'dd/MM/yyyy' }}</td>
            <td>{{ r.createdBy }}<div class="imp-muted">{{ r.createdUtc | date:'dd/MM/yyyy HH:mm' }}</div></td>
            <td><span class="imp-status" [matTooltip]="statusHelp(r.status)">{{ r.status }}</span></td>
            <td><button *ngIf="r.canUndo" mat-stroked-button color="warn" [disabled]="!!busy" (click)="undo(r.runId)">Undo</button></td>
          </tr>
          <tr *ngIf="!runs.length"><td colspan="7" class="imp-muted">No uploads yet.</td></tr>
          </tbody>
        </table>

        <details class="imp-legend">
          <summary>What do these mean?</summary>
          <div class="imp-legend-grid">
            <div>
              <div class="imp-label">Readings (the file's checks)</div>
              <ul>
                <li><b>PASS</b>: nothing to flag.</li>
                <li><b>WARN</b>: can be imported, but something is worth a look (gaps, long flat runs, estimated readings).</li>
                <li><b>FAIL</b>: a problem that would make the figures wrong. The file can't be imported until it is fixed.</li>
              </ul>
            </div>
            <div>
              <div class="imp-label">Status</div>
              <ul>
                <li><b>Previewed</b>: checked only. Nothing was imported.</li>
                <li><b>Applied</b>: imported. Undo removes the readings again.</li>
                <li><b>Undone</b>: imported, then undone. The set-up and saved matches are kept.</li>
                <li><b>AppliedWithErrors</b>, <b>Failed</b>, <b>UndoPartial</b>: something went wrong, and the upload shows what.</li>
              </ul>
            </div>
          </div>
        </details>
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <ng-container *ngIf="tab === 0 && preview && !result">
    <button mat-button (click)="startAgain()" [disabled]="!!busy">Start again</button>
    <button mat-stroked-button (click)="checkPlan()" [disabled]="!!busy">Check plan</button>
    <button mat-flat-button color="primary" (click)="apply()" [disabled]="!canApply"
            [matTooltip]="canApply ? '' : 'Every included meter needs a site, the plan must be checked, and any new site confirmed'">
      Import {{ includedCount }} meter(s)
    </button>
  </ng-container>
  <ng-container *ngIf="tab === 0 && result">
    <button mat-button (click)="startAgain()">Import more files</button>
    <button *ngIf="result.status !== 'Undone' && result.status !== 'Failed'" mat-stroked-button color="warn" (click)="undo(result.runId)" [disabled]="!!busy">Undo this import</button>
  </ng-container>
  <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>
